import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { Info, Plus, Trash2 } from "lucide-react";
import { RootState } from "@/redux/store";
import { cn } from "@/lib/utils";
import { getApiErrorMessage } from "@/utils/apiError";
import {
  BulkFacilityScheduleItem,
  MembershipFacilityWindow,
} from "@/types/membership-facility-schedule";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import Combobox from "@/components/ui/common/combobox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import RecurrenceSelector, {
  RecurrenceConfig,
} from "@/components/admin/facility_schedule/modal/recurrence-selector";
import {
  useBulkSaveMembershipFacilitySchedulesMutation,
  useGetMembershipAvailabilityOptionsQuery,
  useGetMembershipFacilitySchedulesQuery,
} from "@/services/membershipFacilityScheduleApi";
import { generateRRuleString } from "@/utils/scheduleUtils";
import {
  comboboxLabel,
  findComboboxValue,
  groupWindowsByFacility,
  parseRRuleToConfig,
  toComboboxOptions,
  validateAvailabilityItems,
} from "@/utils/availabilityUtils";

const DEFAULT_RRULE = "RRULE:FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU,WE,TH,FR";

/**
 * A window while it is being edited. `clientKey` is a stable React key: unsaved windows have
 * no id, and index keys would hand one window's recurrence-selector state to its neighbour
 * when a window is removed.
 */
type EditableWindow = MembershipFacilityWindow & { clientKey: string };
type EditableItem = { facility_id: number; windows: EditableWindow[] };

// Only advanced from event handlers and effects (never during render), so renders stay pure.
// A counter is used rather than crypto.randomUUID(), which is unavailable when the app is
// served over plain http on a LAN address.
let clientKeySeq = 0;
const withClientKey = (win: MembershipFacilityWindow): EditableWindow => ({
  ...win,
  clientKey: `window-${++clientKeySeq}`,
});

const toEditable = (items: BulkFacilityScheduleItem[]): EditableItem[] =>
  items.map((item) => ({ ...item, windows: item.windows.map(withClientKey) }));

const toPayload = (items: EditableItem[]): BulkFacilityScheduleItem[] =>
  items.map((item) => ({
    facility_id: item.facility_id,
    windows: item.windows.map(({ clientKey: _clientKey, ...win }) => win),
  }));

const getDefaultWindow = (): EditableWindow =>
  withClientKey({
    name: "",
    rrule_str: DEFAULT_RRULE,
    start_time: "08:00",
    end_time: "20:00",
  });

/**
 * Recurrence selector output -> rrule_str. The window's own start/end dates bound the
 * recurrence, so COUNT/UNTIL are never emitted from here.
 */
const configToRRule = (config: RecurrenceConfig, startDate: string): string | null => {
  if (config.frequency === "choose") return null;
  return generateRRuleString({ ...config, endCondition: "on_date", endDate: "" }, startDate);
};

/** Explanatory text lives behind an info icon so the form stays compact. */
const InfoHint = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <TooltipProvider delayDuration={150}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="text-muted-foreground hover:text-foreground align-middle"
        >
          <Info className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-xs leading-relaxed">{children}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const SectionHeader = ({
  icon,
  title,
  info,
  infoLabel,
  action,
}: {
  icon: string;
  title: string;
  info: React.ReactNode;
  infoLabel: string;
  action?: React.ReactNode;
}) => (
  <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
    <div className="flex items-center gap-2">
      <div className="p-2 bg-primary/10 rounded-lg text-primary">
        <i className={`${icon} text-sm`}></i>
      </div>
      <h3 className="font-medium text-gray-900 text-sm italic">{title}</h3>
      <InfoHint label={infoLabel}>{info}</InfoHint>
    </div>
    {action}
  </div>
);

interface WindowRowProps {
  index: number;
  window: MembershipFacilityWindow;
  onChange: (patch: Partial<MembershipFacilityWindow>) => void;
  onRemove: () => void;
}

const WindowRow = ({ index, window: win, onChange, onRemove }: WindowRowProps) => {
  const recurrenceValue = useMemo(
    () => (win.rrule_str ? parseRRuleToConfig(win.rrule_str) : undefined),
    [win.rrule_str]
  );
  // Recurrence anchor only (the server keeps it); never shown as a validity date.
  const anchorDate = win.start_date || format(new Date(), "yyyy-MM-dd");

  return (
    <div className="rounded-md border border-border p-3 space-y-3" data-testid="availability-window">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">Window {index + 1}</p>
          {!win.rrule_str && (
            <InfoHint label={`Window ${index + 1} recurrence information`}>
              No recurrence selected: this window applies every day.
            </InfoHint>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Remove window ${index + 1}`}
          className="h-7 px-2 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <Trash2 className="size-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label htmlFor={`window-${index}-name`}>Name</Label>
          <Input
            id={`window-${index}-name`}
            value={win.name ?? ""}
            placeholder="Optional"
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`window-${index}-start-time`}>Start Time</Label>
          <Input
            id={`window-${index}-start-time`}
            type="time"
            value={win.start_time}
            onChange={(e) => onChange({ start_time: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`window-${index}-end-time`}>End Time</Label>
          <Input
            id={`window-${index}-end-time`}
            type="time"
            value={win.end_time}
            onChange={(e) => onChange({ end_time: e.target.value })}
          />
        </div>
      </div>

      <RecurrenceSelector
        hideEndCondition
        value={recurrenceValue}
        startDate={anchorDate}
        onChange={(config) => onChange({ rrule_str: configToRRule(config, anchorDate) })}
      />
    </div>
  );
};

interface FacilityAvailabilityFormProps {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  /** Membership pre-selected when the drawer opens (the one shown on the calendar). */
  initialMembershipId?: number | null;
  onSaved?: (membershipPlanId: number) => void;
}

const FacilityAvailabilityForm = ({
  isOpen,
  setOpen,
  initialMembershipId = null,
  onSaved,
}: FacilityAvailabilityFormProps) => {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  const [membershipId, setMembershipId] = useState<number | null>(initialMembershipId);
  const [schedules, setSchedules] = useState<EditableItem[]>([]);
  const [activeFacilityId, setActiveFacilityId] = useState<number | null>(null);

  // With a membership chosen, `facilities` holds only the facilities attached to it.
  const { data: options, isFetching: facilitiesLoading } = useGetMembershipAvailabilityOptionsQuery(
    membershipId ? { membership_plan_id: membershipId } : undefined,
    { skip: !isOpen }
  );
  const memberships = options?.memberships ?? [];
  const facilities = useMemo(
    () => (membershipId ? options?.facilities ?? [] : []),
    [membershipId, options]
  );

  const { data: savedWindows, isFetching: isLoadingWindows } =
    useGetMembershipFacilitySchedulesQuery(
      { membership_plan_id: membershipId || undefined },
      { skip: !membershipId || !isOpen }
    );

  const [bulkSave, { isLoading: isSaving }] = useBulkSaveMembershipFacilitySchedulesMutation();

  useEffect(() => {
    if (isOpen) setMembershipId(initialMembershipId);
  }, [isOpen, initialMembershipId]);

  // Selecting a membership loads its saved availability, one item per facility.
  // It is loaded once per selection: a background refetch of the same membership must not
  // replace what the user is editing or move them off the facility tab they are on.
  const loadedMembershipRef = useRef<number | null>(null);
  useEffect(() => {
    if (!membershipId) {
      loadedMembershipRef.current = null;
      setSchedules([]);
      setActiveFacilityId(null);
      return;
    }
    if (loadedMembershipRef.current === membershipId) return;
    // While a new membership loads, the query can still hold the previous one's rows.
    if (isLoadingWindows || savedWindows === undefined) return;

    const rows = savedWindows.filter((w) => w.membership_plan_id === membershipId);
    const grouped = toEditable(groupWindowsByFacility(rows));
    loadedMembershipRef.current = membershipId;
    setSchedules(grouped);
    setActiveFacilityId(grouped[0]?.facility_id ?? null);
  }, [savedWindows, membershipId, isLoadingWindows]);

  const activeSchedule = schedules.find((s) => s.facility_id === activeFacilityId);
  const unconfiguredFacilities = facilities.filter(
    (f) => !schedules.some((s) => s.facility_id === f.id)
  );
  const membershipOptions = useMemo(() => toComboboxOptions(memberships), [memberships]);
  const addFacilityOptions = toComboboxOptions(unconfiguredFacilities);
  const facilityName = (id: number) =>
    facilities.find((f) => f.id === id)?.name ||
    savedWindows?.find((w) => w.facility_id === id)?.facility_name ||
    `Facility #${id}`;

  const updateActiveWindows = (
    updater: (windows: EditableWindow[]) => EditableWindow[]
  ) => {
    if (!activeSchedule) return;
    setSchedules((prev) =>
      prev.map((s) =>
        s.facility_id === activeSchedule.facility_id ? { ...s, windows: updater(s.windows) } : s
      )
    );
  };

  const handleAddFacility = (value: string) => {
    const facilityId = Number(value);
    if (!facilityId || schedules.some((s) => s.facility_id === facilityId)) return;
    setSchedules((prev) => [...prev, { facility_id: facilityId, windows: [getDefaultWindow()] }]);
    setActiveFacilityId(facilityId);
  };

  const handleRemoveFacility = (facilityId: number) => {
    // Kept with no windows so that saving clears this facility's availability.
    setSchedules((prev) =>
      prev.map((s) => (s.facility_id === facilityId ? { ...s, windows: [] } : s))
    );
  };

  const handleClose = () => {
    setOpen(false);
    setMembershipId(null);
    setSchedules([]);
    setActiveFacilityId(null);
  };

  const handleSave = async () => {
    if (!membershipId) {
      toast({ variant: "destructive", title: "Select a membership first." });
      return;
    }
    if (schedules.length === 0) {
      toast({ variant: "destructive", title: "Add at least one facility." });
      return;
    }
    const payload = toPayload(schedules);
    const validationError = validateAvailabilityItems(payload);
    if (validationError) {
      toast({ variant: "destructive", title: "Invalid Availability", description: validationError });
      return;
    }

    try {
      await bulkSave({
        membership_plan_id: membershipId,
        data: { org_id: orgId, schedules: payload },
      }).unwrap();
      toast({ variant: "success", title: "Facility availability saved successfully." });
      onSaved?.(membershipId);
      handleClose();
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Could not save availability",
        description: getApiErrorMessage(error),
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        className={cn("bg-formbg w-full p-0 custom-scrollbar h-full lg:!max-w-[1000px]")}
        hideCloseButton
      >
        <SheetHeader className="px-3 sm:px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
          <div className="flex justify-between items-center gap-2">
            <div className="flex items-center gap-3 min-w-0">
              <button type="button" aria-label="Close" onClick={handleClose}>
                <i className="fa fa-xmark text-xl"></i>
              </button>
              <h1 className="font-medium text-base sm:text-lg truncate">Facility Availability</h1>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" onClick={handleClose} className="h-8 px-2 sm:px-4">
                Cancel
              </Button>
              <LoadingButton
                onClick={handleSave}
                loading={isSaving}
                className="bg-primary text-black h-8 px-3 sm:px-6"
              >
                Save
              </LoadingButton>
            </div>
          </div>
        </SheetHeader>

        <div className="p-3 sm:p-6 space-y-6">
          <Card className="p-3 sm:p-4 lg:p-6 shadow-sm border-l-4 border-l-primary bg-white">
            <SectionHeader
              icon="fa-solid fa-id-card"
              title="Membership"
              infoLabel="About facility availability"
              info={
                <>
                  Availability does not create slots. It limits which existing facility schedule
                  slots members of this membership can book. For Member Only facilities, slots
                  outside the availability are hidden. For Public facilities, members still see
                  every slot but can only pay with credits inside the availability.
                </>
              }
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Membership</Label>
                <Combobox
                  label="membership"
                  showLabel={false}
                  list={membershipOptions}
                  defaultValue={comboboxLabel(membershipOptions, membershipId)}
                  setFilter={(label: string) => {
                    const id = findComboboxValue(membershipOptions, label);
                    if (id) setMembershipId(Number(id));
                  }}
                />
              </div>
            </div>
          </Card>

          <Card className="p-3 sm:p-4 lg:p-6 shadow-sm border-l-4 border-l-primary bg-white">
            <SectionHeader
              icon="fa-solid fa-building"
              title="Facilities"
              infoLabel="About availability windows"
              info={
                <>
                  Add a facility, then define one or more windows for it: the days it repeats on and
                  a time range. A slot is available when it falls fully inside any window. Windows
                  have no start or end date; they apply for as long as the member's membership is
                  active. To change or remove a single day, click it on the calendar. Saving
                  replaces the facility's windows with the ones shown here.
                </>
              }
              action={
                <div className="w-[220px]">
                  <Combobox
                    // Remount after each pick so the control goes back to "Select facility to add".
                    key={`add-facility-${membershipId}-${schedules.length}`}
                    label="facility to add"
                    showLabel={false}
                    className="h-8"
                    list={addFacilityOptions}
                    defaultValue=""
                    disabled={!membershipId || facilitiesLoading || addFacilityOptions.length === 0}
                    setFilter={(label: string) => {
                      const id = findComboboxValue(addFacilityOptions, label);
                      if (id) handleAddFacility(id);
                    }}
                  />
                </div>
              }
            />

            {!membershipId ? (
              <p className="text-sm text-muted-foreground">Select a membership to begin.</p>
            ) : isLoadingWindows ? (
              <p className="text-sm text-muted-foreground">Loading availability...</p>
            ) : schedules.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {facilitiesLoading || facilities.length > 0
                  ? "No facilities yet. Pick a facility to add and define its availability."
                  : "This membership has no facilities. Add facilities to the membership first."}
              </p>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2" role="tablist" aria-label="Facilities">
                  {schedules.map((item) => {
                    const selected = item.facility_id === activeFacilityId;
                    return (
                      <button
                        key={item.facility_id}
                        type="button"
                        role="tab"
                        aria-selected={selected}
                        onClick={() => setActiveFacilityId(item.facility_id)}
                        className={cn(
                          "inline-flex items-center gap-2 px-3 py-1 rounded-md border text-sm",
                          selected
                            ? "bg-primary/10 border-primary text-primary"
                            : "bg-white border-border hover:bg-secondary"
                        )}
                      >
                        {facilityName(item.facility_id)}
                        <span className="text-xs text-muted-foreground">({item.windows.length})</span>
                      </button>
                    );
                  })}
                </div>

                {activeSchedule && (
                  <div className="space-y-3">
                    {activeSchedule.windows.map((win, index) => (
                      <WindowRow
                        key={win.clientKey}
                        index={index}
                        window={win}
                        onChange={(patch) =>
                          updateActiveWindows((windows) =>
                            windows.map((w, i) => (i === index ? { ...w, ...patch } : w))
                          )
                        }
                        onRemove={() =>
                          updateActiveWindows((windows) => windows.filter((_, i) => i !== index))
                        }
                      />
                    ))}
                    {activeSchedule.windows.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        No windows: saving removes this membership's availability for{" "}
                        {facilityName(activeSchedule.facility_id)}.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          updateActiveWindows((windows) => [...windows, getDefaultWindow()])
                        }
                      >
                        <Plus className="size-4 mr-1" />
                        Add window
                      </Button>
                      {activeSchedule.windows.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoveFacility(activeSchedule.facility_id)}
                        >
                          <Trash2 className="size-4 mr-1" />
                          Remove all windows
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FacilityAvailabilityForm;
